import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, apiFetch, getAdminTeams } from "@/lib/api";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { unscopedKey, useScopedKey } from "@/lib/queryClient";
import type { Match, AdminStreamSession, Season } from "@/lib/types";
import { useMe } from "@/features/auth/useMe";
import { useAuthStore } from "@/stores/auth";
import { useScopeStore } from "@/stores/scope";
import { MatchEditModal } from "@/features/matches/components/MatchEditModal";
import { TkticSelect } from "@/components/ui/TkticSelect";
import { useUploadQueueContext } from "@/features/coach/components/UploadQueueContext";
import type { UploadSlot } from "@/features/coach/components/useUploadQueue";
import {
  defaultSlotForMatch,
  SLOT_LABELS,
  slotsForMatch,
} from "@/features/matches/matchSlots";
import {
  useAdminStreams,
  useTranscodeProgress,
} from "../hooks/useAdminData";
import { MatchSlotPanel } from "./MatchSlotPanel";

/**
 * M8a B1–B9 — `/admin/matches`.
 *
 * Library table (8 columns) + filters + per-row expand to a slot
 * diagnostics panel with the operator recovery action set:
 *   - Verify / Regen HLS / Re-transcode / Retry / Force Re-transcode
 *   - Logs / Regenerate Thumbnail
 *   - Delete match (B7)
 *
 * VOD viewer counts read from the shell's already-running
 * `useAdminStreams()` cache, filtered to `kind='vod'` per match.
 * Live transcode progress is polled ONLY while at least one slot
 * is `transcoding` (the route gate avoids polling when idle).
 *
 * SCOPE — `/admin/matches` is visible to **global admins, team
 * admins, and uploaders**. ALL users default to the active team
 * from the header switcher; this keeps the visible match list
 * consistent with the team chip in the header (previously global
 * admins always saw cross-tenant rows, which silently ignored their
 * scope selection). A "Show all teams" toggle is offered to global
 * admins only — it flips the fetch to unscoped so they can still
 * investigate cross-tenant issues without round-tripping through
 * the header switcher. The in-page Team filter dropdown only
 * surfaces in "show all teams" mode where it actually does work.
 *
 * Calling `/api/matches` without `team_id` for a non-global user
 * would return the global recent-matches list and leak cross-tenant
 * match metadata, so non-global queries always pass scope.
 *
 * The Add/Edit modal (B6) is heavy enough that it lands in a
 * follow-up commit on this branch.
 */
/** Stable empty-array identity so the `me.seasons` selector doesn't
 * return a fresh `[]` each render (which would re-fire `useMemo`). */
const EMPTY_SEASONS: Season[] = [];

export function AdminMatchesPage() {
  const me = useMe().data;
  const isGlobalAdmin = !!me?.user?.is_global_admin;
  const teamId = useScopeStore((s) => s.teamId);

  // Default to the header-switcher scope for EVERYONE (consistent
  // with what the chip says). Global admins can opt into a
  // cross-tenant view via the "Show all teams" toggle below — that's
  // when the unscoped admin cache kicks in. The unscoped cache is
  // private data; logout evicts it via `removeAuthenticatedFromCache`
  // before a next user can inherit rows.
  const [showAllTeams, setShowAllTeams] = useState(false);
  const useUnscopedFetch = isGlobalAdmin && showAllTeams;

  const unscopedMatchesQuery = useQuery({
    queryKey: unscopedKey("admin-matches"),
    queryFn: () => apiFetch<Match[]>("/api/matches", { scope: false }),
    enabled: useUnscopedFetch,
  });
  const scopedMatchesQuery = useQuery({
    queryKey: useScopedKey("admin-matches-scoped"),
    // Scope by team only — `seasonId: null` suppresses the season query
    // param so the list shows the team's matches across every season
    // (seasons are globally shared; the season name is an inline row
    // detail, not a filter). Injecting `season_id` would hide matches
    // that the unscoped public site still shows.
    queryFn: () =>
      apiFetch<Match[]>("/api/matches", { scope: { teamId, seasonId: null } }),
    enabled: !useUnscopedFetch && !!teamId,
  });
  const matchesQuery = useUnscopedFetch ? unscopedMatchesQuery : scopedMatchesQuery;
  const streamsQuery = useAdminStreams();
  const matches = matchesQuery.data ?? [];

  // Global admins see cross-tenant rows in "Show all teams" mode;
  // load the team list so each row can label which team it belongs
  // to and feed the in-page team filter. Only fetched when the
  // toggle is on — when scoped, the page already shows one team's
  // matches so the column + filter would be redundant.
  const adminTeamsQuery = useQuery({
    queryKey: unscopedKey("admin-teams"),
    queryFn: getAdminTeams,
    enabled: useUnscopedFetch,
    staleTime: 60_000,
  });
  const teamNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const t of adminTeamsQuery.data ?? []) map.set(t.id, t.name);
    return map;
  }, [adminTeamsQuery.data]);

  // Gate transcode-progress polling on "any visible slot transcoding".
  const anyTranscoding = useMemo(() => {
    for (const m of matches) {
      for (const status of Object.values(m.video_status ?? {})) {
        if (status === "transcoding") return true;
      }
    }
    return false;
  }, [matches]);
  useTranscodeProgress({ enabled: anyTranscoding });

  // B2 — filters.
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<
    "all" | "none" | "ready" | "transcoding" | "error" | "partial"
  >("all");
  const [formatFilter, setFormatFilter] = useState<
    "all" | "full" | "two_halves"
  >("all");
  const [teamFilter, setTeamFilter] = useState<string>("all");
  const [seasonFilter, setSeasonFilter] = useState<string>("all");
  const [sort, setSort] = useState<"date_asc" | "date_desc" | "updated_desc">(
    "date_desc",
  );

  // Season options come from the globally-shared `me.seasons` list —
  // the same source `SeasonLabel` resolves names from, so no extra
  // fetch. The default season is marked so the operator can tell which
  // one new matches inherit.
  const seasons = useAuthStore((s) => s.me?.seasons ?? EMPTY_SEASONS);
  const seasonOptions = useMemo(
    () => [
      { value: "all", label: "All seasons" },
      ...seasons.map((s) => ({
        value: s.id,
        label: s.is_default ? `${s.name} (default)` : s.name,
      })),
    ],
    [seasons],
  );

  const [expanded, setExpanded] = useState<string | null>(null);
  const [editTarget, setEditTarget] = useState<Match | "new" | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Match | null>(null);
  // Sequential multi-upload queue: per-row "Upload video" adds an item.
  // The queue lives in `UploadQueueProvider` at AdminShell level so it
  // (and the docked panel) survives navigation between admin sections.
  const uploadQueue = useUploadQueueContext();
  const [queueTarget, setQueueTarget] = useState<Match | null>(null);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const filtered = matches.filter((m) => {
      if (term) {
        const haystack = [
          m.home_team,
          m.away_team,
          m.location,
          m.id,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      if (formatFilter !== "all" && deriveFormatGroup(m) !== formatFilter) {
        return false;
      }
      if (statusFilter !== "all") {
        const agg = deriveAggregateStatus(m);
        if (agg !== statusFilter) return false;
      }
      if (teamFilter !== "all") {
        const tid = m.team_id ?? "__unassigned__";
        if (tid !== teamFilter) return false;
      }
      if (seasonFilter !== "all" && m.season_id !== seasonFilter) {
        return false;
      }
      return true;
    });

    const sorted = [...filtered];
    sorted.sort((a, b) => {
      if (sort === "updated_desc") {
        return (b.updated_at ?? "").localeCompare(a.updated_at ?? "");
      }
      const da = a.date || "";
      const db = b.date || "";
      return sort === "date_asc" ? da.localeCompare(db) : db.localeCompare(da);
    });
    return sorted;
  }, [matches, search, statusFilter, formatFilter, teamFilter, seasonFilter, sort]);

  // Detect cross-tenant rows lacking a team_id so global admins can
  // filter for / spot orphaned matches that predate team scoping.
  // Only meaningful in "show all teams" mode — scoped fetches don't
  // surface untenanted rows.
  const hasUnassigned = useMemo(
    () => useUnscopedFetch && matches.some((m) => !m.team_id),
    [useUnscopedFetch, matches],
  );

  return (
    <div data-testid="admin-matches" className="flex flex-col gap-3">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-base font-display uppercase tracking-wider text-ink">
            Matches
          </h2>
          <p className="mt-0.5 text-[0.65rem] font-mono uppercase tracking-wide text-ink-muted">
            Create, edit, upload, and recover public match rows.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-mono text-ink-muted">
            {matchesQuery.isFetching ? "Refreshing…" : `${filtered.length} of ${matches.length}`}
          </span>
          <button
            type="button"
            onClick={() => setEditTarget("new")}
            data-testid="admin-matches-add"
            className="rounded border border-accent/50 bg-accent/15 px-3 py-1.5 text-[0.65rem] font-display uppercase tracking-wider text-accent hover:bg-accent/25 focus:outline-none focus:ring-2 focus:ring-accent"
          >
            Add Match
          </button>
        </div>
      </header>

      {matchesQuery.error ? (
        <p
          data-testid="admin-matches-error"
          className="text-destructive font-mono text-xs"
        >
          Failed to load matches.
        </p>
      ) : null}

      {/* B2 — filters. One horizontal control row: search + the four
       * filter selects + the global-admin "Show all teams" toggle.
       * The search field `grow`s to absorb all free width, so the
       * four fixed-width selects are pushed right and sit flush
       * against the toggle — no dead gap in the middle. `flex-wrap`
       * still lets it reflow on narrow widths.
       *
       * Each `TkticSelect` is wrapped in a fixed-width `div`. The
       * select's trigger is `w-full` by design (fills its parent);
       * dropping a `w-*` onto the trigger via `className` does NOT
       * reliably override `w-full` — both classes have equal
       * specificity and Tailwind's stylesheet order decides, so
       * `w-full` keeps winning and every select stretches to 100% of
       * the row and wraps. Sizing the WRAPPER instead makes `w-full`
       * resolve to the wrapper's width, which is what we want. */}
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-rule bg-bg-card/40 p-2">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search teams / location / id"
          data-testid="admin-matches-search"
          className="bg-surface border border-rule rounded px-2 py-1.5 text-xs grow min-w-[12rem] focus:outline-none focus:ring-2 focus:ring-accent"
        />
        <div className="w-full sm:w-36">
          <TkticSelect
            value={statusFilter}
            onChange={(next) => setStatusFilter(next as typeof statusFilter)}
            data-testid="admin-matches-status-filter"
            size="sm"
            ariaLabel="Filter by status"
            options={[
              { value: "all", label: "All status" },
              { value: "ready", label: "Ready" },
              { value: "transcoding", label: "Transcoding" },
              { value: "error", label: "Error" },
              { value: "partial", label: "Partial" },
              { value: "none", label: "No video" },
            ]}
          />
        </div>
        <div className="w-full sm:w-32">
          <TkticSelect
            value={formatFilter}
            onChange={(next) => setFormatFilter(next as typeof formatFilter)}
            data-testid="admin-matches-format-filter"
            size="sm"
            ariaLabel="Filter by format"
            options={[
              { value: "all", label: "Any format" },
              { value: "full", label: "Full" },
              { value: "two_halves", label: "Halves" },
            ]}
          />
        </div>
        <div className="w-full sm:w-44">
          <TkticSelect
            value={seasonFilter}
            onChange={(next) => setSeasonFilter(next)}
            data-testid="admin-matches-season-filter"
            size="sm"
            ariaLabel="Filter by season"
            options={seasonOptions}
          />
        </div>
        {useUnscopedFetch ? (
          <div className="w-full sm:w-44">
            <TkticSelect
              value={teamFilter}
              onChange={(next) => setTeamFilter(next)}
              data-testid="admin-matches-team-filter"
              size="sm"
              ariaLabel="Filter by team"
              options={[
                { value: "all", label: "All teams" },
                ...(adminTeamsQuery.data ?? [])
                  .slice()
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((t) => ({ value: t.id, label: t.name })),
                ...(hasUnassigned
                  ? [{ value: "__unassigned__", label: "(no team)" }]
                  : []),
              ]}
            />
          </div>
        ) : null}
        <div className="w-full sm:w-44">
          <TkticSelect
            value={sort}
            onChange={(next) => setSort(next as typeof sort)}
            data-testid="admin-matches-sort"
            size="sm"
            ariaLabel="Sort matches"
            options={[
              { value: "date_desc", label: "Newest first" },
              { value: "date_asc", label: "Oldest first" },
              { value: "updated_desc", label: "Recently updated" },
            ]}
          />
        </div>
        {isGlobalAdmin ? (
          <button
            type="button"
            onClick={() => {
              setShowAllTeams((v) => !v);
              setTeamFilter("all");
            }}
            data-testid="admin-matches-show-all-teams"
            aria-pressed={showAllTeams}
            className={`rounded border px-2 py-1.5 text-[0.65rem] font-display uppercase tracking-wider transition-colors focus:outline-none focus:ring-2 focus:ring-accent ${
              showAllTeams
                ? "border-accent/50 bg-accent/15 text-accent hover:bg-accent/25"
                : "border-rule text-ink-muted hover:bg-surface-muted"
            }`}
          >
            {showAllTeams ? "Showing all teams" : "Show all teams"}
          </button>
        ) : null}
      </div>

      {/* B1 — library table */}
      <div className="hidden overflow-x-auto rounded-lg border border-rule bg-bg-card/40 md:block">
        <table data-testid="admin-matches-table" className="w-full text-xs">
          <thead className="border-b border-rule text-ink-muted">
            <tr>
              <th className="text-left px-2 py-1.5 w-6"></th>
              <th className="text-left px-2 py-1.5">Date</th>
              {useUnscopedFetch ? (
                <th className="text-left px-2 py-1.5">Team</th>
              ) : null}
              <th className="text-left px-2 py-1.5">Matchup</th>
              <th className="text-left px-2 py-1.5">Format</th>
              <th className="text-left px-2 py-1.5">Slots</th>
              <th className="text-left px-2 py-1.5">Score</th>
              <th className="text-left px-2 py-1.5">Watching</th>
              <th className="text-right px-2 py-1.5">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td
                  colSpan={useUnscopedFetch ? 9 : 8}
                  data-testid="admin-matches-empty"
                  className="text-center text-ink-muted px-3 py-6"
                >
                  No matches.
                </td>
              </tr>
            ) : null}
            {filtered.map((m) => {
              const isOpen = expanded === m.id;
              const watchers = countVodViewers(streamsQuery.data?.active, m.id);
              const teamName = useUnscopedFetch
                ? m.team_id
                  ? teamNameById.get(m.team_id) ?? m.team_id
                  : null
                : null;
              return (
                <FragmentRow
                  key={m.id}
                  match={m}
                  isOpen={isOpen}
                  onToggle={() => setExpanded(isOpen ? null : m.id)}
                  onEdit={() => setEditTarget(m)}
                  onDelete={() => setDeleteTarget(m)}
                  onUpload={() => setQueueTarget(m)}
                  watchers={watchers}
                  showTeamColumn={useUnscopedFetch}
                  teamName={teamName}
                />
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="flex flex-col gap-2 md:hidden" data-testid="admin-matches-cards">
        {filtered.length === 0 ? (
          <p
            data-testid="admin-matches-empty-mobile"
            className="rounded-lg border border-rule bg-bg-card/40 px-3 py-6 text-center text-ink-muted"
          >
            No matches.
          </p>
        ) : null}
        {filtered.map((m) => {
          const isOpen = expanded === m.id;
          const watchers = countVodViewers(streamsQuery.data?.active, m.id);
          const teamName = useUnscopedFetch
            ? m.team_id
              ? teamNameById.get(m.team_id) ?? m.team_id
              : null
            : null;
          return (
            <AdminMatchCard
              key={m.id}
              match={m}
              isOpen={isOpen}
              onToggle={() => setExpanded(isOpen ? null : m.id)}
              onEdit={() => setEditTarget(m)}
              onDelete={() => setDeleteTarget(m)}
              onUpload={() => setQueueTarget(m)}
              watchers={watchers}
              showTeamColumn={useUnscopedFetch}
              teamName={teamName}
            />
          );
        })}
      </div>

      {/* B6 — add/edit match form (M12b: extracted into shared
       * `MatchEditModal` so the coach Matches tab + admin Matches page
       * share the same modal). */}
      {editTarget ? (
        <MatchEditModal
          match={editTarget === "new" ? null : editTarget}
          onClose={() => setEditTarget(null)}
        />
      ) : null}

      {/* B7 — delete confirm */}
      {deleteTarget ? (
        <DeleteMatchConfirm
          match={deleteTarget}
          onClose={() => setDeleteTarget(null)}
        />
      ) : null}

      {/* Per-row "Upload video" → slot + file picker → adds to the queue.
       * The docked `UploadQueuePanel` is rendered by `AdminShell`, not
       * here, so it persists across admin route changes. */}
      {queueTarget ? (
        <AddToQueueModal
          match={queueTarget}
          onAdd={(slot, file) => {
            uploadQueue.enqueue(queueTarget, slot, file);
            setQueueTarget(null);
          }}
          onClose={() => setQueueTarget(null)}
        />
      ) : null}
    </div>
  );
}

/** Slot + file picker for adding one video to the upload queue. Kept
 * deliberately small — the heavy chunked-upload UX lives in the docked
 * `UploadQueuePanel`; this modal just captures {slot, file}.
 *
 * Focus handling mirrors `CoachMatchUploadModal`: focus the first field
 * on open, restore focus to the trigger on close, window-level Escape.
 * Tab is contained within the dialog so a keyboard user can't drift
 * onto the (aria-hidden-equivalent) background behind an `aria-modal`. */
function AddToQueueModal({
  match,
  onAdd,
  onClose,
}: {
  match: Match;
  onAdd: (slot: UploadSlot, file: File) => void;
  onClose: () => void;
}) {
  // Slot options follow the match's video layout — a `two_halves`
  // match has no `full` slot, so offering it would queue an upload
  // for a slot the match can't have. State seeds to the match's
  // first valid slot.
  const slotOptions = slotsForMatch(match);
  const [slot, setSlot] = useState<UploadSlot>(
    () => defaultSlotForMatch(match) as UploadSlot,
  );
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLFormElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  // Initial focus + restore-on-close. The trigger is the row's
  // "Upload video" button; returning focus there keeps keyboard nav
  // coherent after the modal unmounts.
  useEffect(() => {
    previousFocusRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    fileInputRef.current?.focus();
    const previousFocus = previousFocusRef.current;
    return () => {
      previousFocus?.focus();
    };
  }, []);

  // Window-level Escape so the modal closes even when focus has not
  // (yet) landed inside the overlay subtree.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Tab containment: wrap focus between the first and last focusable
  // element inside the dialog so an `aria-modal` user can't tab onto
  // the background.
  const onDialogKeyDown = (event: React.KeyboardEvent<HTMLFormElement>) => {
    if (event.key !== "Tab") return;
    const root = dialogRef.current;
    if (!root) return;
    const focusable = root.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    );
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;
    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!file) {
      setError("Pick a video file (.mp4 or .mkv) first.");
      return;
    }
    onAdd(slot, file);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Add match video to upload queue"
      data-testid="admin-add-to-queue-modal"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      className="fixed inset-0 z-[140] flex items-end justify-center bg-black/80 p-3 sm:items-center sm:p-4"
    >
      <form
        ref={dialogRef}
        onSubmit={onSubmit}
        onKeyDown={onDialogKeyDown}
        className="flex max-h-[calc(100dvh-1.5rem)] w-full max-w-md flex-col gap-4 overflow-y-auto rounded-xl border border-rule bg-surface p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] shadow-2xl sm:p-5"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-base font-display uppercase tracking-wider text-ink">
              Queue match video
            </h3>
            <p className="mt-1 truncate text-xs text-ink-muted">
              {match.home_team || "—"} vs {match.away_team || "—"}
              {match.date ? ` · ${match.date}` : ""}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="admin-add-to-queue-close"
            className="rounded border border-rule px-2 py-1 text-xs text-ink-muted hover:text-ink"
          >
            Close
          </button>
        </div>

        <label className="flex flex-col gap-1 text-xs text-ink-muted">
          <span className="font-display uppercase tracking-wide">Slot</span>
          <TkticSelect
            value={slot}
            onChange={(next) => setSlot(next as UploadSlot)}
            data-testid="admin-add-to-queue-slot"
            ariaLabel="Slot"
            className="w-full"
            options={slotOptions.map((value) => ({
              value,
              label: SLOT_LABELS[value],
            }))}
          />
        </label>

        <label className="flex flex-col gap-1 text-xs text-ink-muted">
          <span className="font-display uppercase tracking-wide">
            Video file (.mp4 or .mkv)
          </span>
          {/* Native file pickers can't be replaced entirely (browser
            * security forbids triggering them from synthetic events),
            * but the `file:` Tailwind variants restyle the inner button
            * into a brand accent pill — same pattern as
            * `CoachMatchUploadModal` + `MatchEditModal`'s opponent-logo
            * picker. Mirrors the no-native-browser-chrome rule. */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".mp4,.mkv,video/mp4,video/x-matroska"
            onChange={(e) => {
              setFile(e.target.files?.[0] ?? null);
              setError(null);
            }}
            data-testid="admin-add-to-queue-file"
            className="block w-full text-xs text-ink file:mr-2 file:rounded file:border-0 file:bg-accent/15 file:px-2 file:py-1 file:text-[0.65rem] file:font-display file:uppercase file:tracking-wider file:text-accent hover:file:bg-accent/25"
          />
          {file ? (
            <span className="mt-1 font-mono text-[0.6rem] text-ink-muted">
              {(file.size / 1024 / 1024).toFixed(1)} MB
            </span>
          ) : null}
        </label>

        {error ? (
          <p
            role="alert"
            data-testid="admin-add-to-queue-error"
            className="rounded border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive"
          >
            {error}
          </p>
        ) : null}

        <div className="flex flex-col-reverse gap-2 border-t border-rule pt-3 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-rule bg-bg-card px-3 py-1.5 text-sm font-display uppercase tracking-wide text-ink hover:bg-surface"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!file}
            data-testid="admin-add-to-queue-submit"
            className="rounded bg-accent px-3 py-1.5 text-sm font-display uppercase tracking-wide text-white hover:opacity-90 disabled:opacity-60"
          >
            Add to queue
          </button>
        </div>
      </form>
    </div>
  );
}

function FragmentRow({
  match,
  isOpen,
  onToggle,
  onEdit,
  onDelete,
  onUpload,
  watchers,
  showTeamColumn,
  teamName,
}: {
  match: Match;
  isOpen: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onUpload: () => void;
  watchers: number;
  showTeamColumn: boolean;
  teamName: string | null;
}) {
  // Slots come from the match's video LAYOUT (`format`), not from
  // whatever keys `video_status` happens to carry — a pre-migration
  // `two_halves` match may still have a stale `full` key, and we
  // must not surface it.
  const slots = slotsForMatch(match);
  const aggStatus = deriveAggregateStatus(match);

  return (
    <>
      <tr
        data-testid="admin-matches-row"
        data-match-id={match.id}
        data-aggregate-status={aggStatus}
        className="border-t border-rule/60 hover:bg-surface/30"
      >
        <td className="px-2 py-1.5 align-top">
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={isOpen}
            aria-label={isOpen ? "Collapse diagnostics" : "Expand diagnostics"}
            data-testid="admin-matches-row-expand"
            className="text-ink-muted hover:text-ink font-mono"
          >
            {isOpen ? "▼" : "▶"}
          </button>
        </td>
        <td className="px-2 py-1.5 align-top font-mono">
          {match.date || "—"}
          {match.time ? (
            <div className="text-[0.6rem] text-ink-muted">{match.time}</div>
          ) : null}
        </td>
        {showTeamColumn ? (
          <td
            data-testid="admin-matches-row-team"
            data-team-id={match.team_id ?? ""}
            className="px-2 py-1.5 align-top"
          >
            {teamName ? (
              <span className="text-ink">{teamName}</span>
            ) : (
              <span className="text-ink-muted italic">no team</span>
            )}
          </td>
        ) : null}
        <td className="px-2 py-1.5 align-top">
          <div className="text-ink">
            {match.home_team || "—"}{" "}
            <span className="text-ink-muted">vs</span>{" "}
            {match.away_team || "—"}
          </div>
          {match.location ? (
            <div className="text-[0.6rem] text-ink-muted">{match.location}</div>
          ) : null}
          <SeasonLabel match={match} />
        </td>
        <td className="px-2 py-1.5 align-top font-mono">
          <FormatPill format={match.format} />
        </td>
        <td className="px-2 py-1.5 align-top">
          <div className="flex flex-wrap gap-1">
            {slots.map((slot) => (
              <SlotPill
                key={slot}
                slot={slot}
                status={match.video_status?.[slot] ?? "none"}
              />
            ))}
          </div>
        </td>
        <td className="px-2 py-1.5 align-top font-mono">
          {match.score_home == null || match.score_away == null
            ? "—"
            : `${match.score_home}–${match.score_away}`}
        </td>
        <td className="px-2 py-1.5 align-top font-mono">
          <span
            data-testid="admin-matches-row-watchers"
            className={watchers > 0 ? "text-accent" : "text-ink-muted"}
          >
            {watchers}
          </span>
        </td>
        <td className="px-2 py-1.5 align-top text-right">
          <div className="inline-flex gap-1">
            <a
              href={`/match/${match.slug}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="admin-matches-row-open"
              className="rounded border border-rule text-ink-muted hover:text-ink px-2 py-0.5 text-[0.65rem] font-display uppercase tracking-wider"
            >
              Open
            </a>
            <button
              type="button"
              onClick={onEdit}
              data-testid="admin-matches-row-edit"
              className="rounded border border-rule text-ink-muted hover:text-ink px-2 py-0.5 text-[0.65rem] font-display uppercase tracking-wider"
            >
              Edit
            </button>
            <button
              type="button"
              onClick={onUpload}
              data-testid="admin-matches-row-upload"
              className="rounded border border-accent/50 bg-accent/15 text-accent hover:bg-accent/25 px-2 py-0.5 text-[0.65rem] font-display uppercase tracking-wider"
            >
              Upload video
            </button>
            <button
              type="button"
              onClick={onDelete}
              data-testid="admin-matches-row-delete"
              className="rounded border border-destructive/40 text-destructive hover:bg-destructive/10 px-2 py-0.5 text-[0.65rem] font-display uppercase tracking-wider"
            >
              Delete
            </button>
          </div>
        </td>
      </tr>
      {isOpen ? (
        <tr
          data-testid="admin-matches-row-diagnostics"
          data-match-id={match.id}
          className="border-t border-rule/40 bg-surface/30"
        >
          <td colSpan={showTeamColumn ? 9 : 8} className="px-3 py-3">
            <MatchSlotPanel match={match} />
          </td>
        </tr>
      ) : null}
    </>
  );
}

function AdminMatchCard({
  match,
  isOpen,
  onToggle,
  onEdit,
  onDelete,
  onUpload,
  watchers,
  showTeamColumn,
  teamName,
}: {
  match: Match;
  isOpen: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onUpload: () => void;
  watchers: number;
  showTeamColumn: boolean;
  teamName: string | null;
}) {
  // Slots come from the match's video LAYOUT (`format`), not from
  // whatever keys `video_status` happens to carry — a pre-migration
  // `two_halves` match may still have a stale `full` key, and we
  // must not surface it.
  const slots = slotsForMatch(match);
  const aggStatus = deriveAggregateStatus(match);

  return (
    <article
      data-testid="admin-matches-card"
      data-match-id={match.id}
      data-aggregate-status={aggStatus}
      className="rounded-lg border border-rule bg-bg-card/40 p-3"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-mono text-[0.65rem] uppercase tracking-wider text-ink-muted">
            {match.date || "No date"}
            {match.time ? <span> · {match.time}</span> : null}
          </p>
          <h3 className="mt-1 font-display text-base text-ink">
            {match.home_team || "—"}{" "}
            <span className="text-ink-muted">vs</span>{" "}
            {match.away_team || "—"}
          </h3>
          {match.location ? (
            <p className="mt-0.5 text-xs text-ink-muted">{match.location}</p>
          ) : null}
          {showTeamColumn ? (
            <p
              data-testid="admin-matches-card-team"
              data-team-id={match.team_id ?? ""}
              className="mt-1 text-xs text-ink-muted"
            >
              Team: <span className="text-ink">{teamName ?? "no team"}</span>
            </p>
          ) : null}
          <SeasonLabel match={match} />
        </div>
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={isOpen}
          aria-label={isOpen ? "Collapse diagnostics" : "Expand diagnostics"}
          data-testid="admin-matches-card-expand"
          className="shrink-0 rounded border border-rule px-2 py-1 font-mono text-xs text-ink-muted hover:text-ink"
        >
          {isOpen ? "Hide" : "Details"}
        </button>
      </div>

      <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
        <div className="rounded border border-rule/60 bg-surface/40 px-2 py-1.5">
          <span className="block font-display text-[0.6rem] uppercase tracking-wider text-ink-muted">
            Format
          </span>
          <FormatPill format={match.format} />
        </div>
        <div className="rounded border border-rule/60 bg-surface/40 px-2 py-1.5">
          <span className="block font-display text-[0.6rem] uppercase tracking-wider text-ink-muted">
            Watching
          </span>
          <span
            data-testid="admin-matches-card-watchers"
            className={watchers > 0 ? "font-mono text-accent" : "font-mono text-ink-muted"}
          >
            {watchers}
          </span>
        </div>
        <div className="rounded border border-rule/60 bg-surface/40 px-2 py-1.5">
          <span className="block font-display text-[0.6rem] uppercase tracking-wider text-ink-muted">
            Score
          </span>
          <span className="font-mono text-ink">
            {match.score_home == null || match.score_away == null
              ? "—"
              : `${match.score_home}–${match.score_away}`}
          </span>
        </div>
        <div className="rounded border border-rule/60 bg-surface/40 px-2 py-1.5">
          <span className="block font-display text-[0.6rem] uppercase tracking-wider text-ink-muted">
            Slots
          </span>
          <div className="mt-1 flex flex-wrap gap-1">
            {slots.length === 0 ? (
              <span className="text-ink-muted text-[0.6rem]">none</span>
            ) : (
              slots.map((slot) => (
                <SlotPill
                  key={slot}
                  slot={slot}
                  status={match.video_status?.[slot] ?? "none"}
                />
              ))
            )}
          </div>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap gap-1 border-t border-rule/60 pt-2">
        <a
          href={`/match/${match.slug}`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="admin-matches-card-open"
          className="rounded border border-rule px-2 py-1 text-[0.65rem] font-display uppercase tracking-wider text-ink-muted hover:text-ink"
        >
          Open
        </a>
        <button
          type="button"
          onClick={onEdit}
          data-testid="admin-matches-card-edit"
          className="rounded border border-rule px-2 py-1 text-[0.65rem] font-display uppercase tracking-wider text-ink-muted hover:text-ink"
        >
          Edit
        </button>
        <button
          type="button"
          onClick={onUpload}
          data-testid="admin-matches-card-upload"
          className="rounded border border-accent/50 bg-accent/15 px-2 py-1 text-[0.65rem] font-display uppercase tracking-wider text-accent hover:bg-accent/25"
        >
          Upload video
        </button>
        <button
          type="button"
          onClick={onDelete}
          data-testid="admin-matches-card-delete"
          className="rounded border border-destructive/40 px-2 py-1 text-[0.65rem] font-display uppercase tracking-wider text-destructive hover:bg-destructive/10"
        >
          Delete
        </button>
      </div>

      {isOpen ? (
        <div
          data-testid="admin-matches-card-diagnostics"
          data-match-id={match.id}
          className="mt-3 border-t border-rule/60 pt-3"
        >
          <MatchSlotPanel match={match} />
        </div>
      ) : null}
    </article>
  );
}

/** Inline season label rendered under the matchup cell. Seasons are
 * globally shared, so the name is resolved from the /api/me payload's
 * top-level `seasons` array. Cheap — no extra fetch. */
function SeasonLabel({ match }: { match: Match }) {
  const seasonName = useAuthStore((s) => {
    if (!s.me || !match.season_id) return null;
    return s.me.seasons.find((sn) => sn.id === match.season_id)?.name ?? null;
  });
  if (!seasonName) return null;
  return (
    <div
      data-testid="admin-matches-row-season"
      className="text-[0.6rem] text-ink-muted"
    >
      Season: {seasonName}
    </div>
  );
}

function FormatPill({ format }: { format: string }) {
  const display = format === "two_halves" ? "Halves" : format === "full" ? "Full" : format;
  return (
    <span className="rounded border border-rule bg-surface px-1.5 py-0.5 text-[0.6rem] uppercase tracking-wider text-ink-muted">
      {display}
    </span>
  );
}

function SlotPill({ slot, status }: { slot: string; status: string }) {
  const tone =
    status === "ready"
      ? "border-good/40 text-good"
      : status === "transcoding"
        ? "border-accent/40 text-accent"
        : status === "error"
          ? "border-destructive/40 text-destructive"
          : "border-rule text-ink-muted";
  return (
    <span
      data-testid="admin-matches-slot-pill"
      data-slot={slot}
      data-slot-status={status}
      className={`rounded border bg-bg-card/40 px-1.5 py-0.5 text-[0.6rem] font-mono ${tone}`}
    >
      {slot.replace("_", " ")} · {status}
    </span>
  );
}

async function invalidateAdminMatches(queryClient: ReturnType<typeof useQueryClient>) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: unscopedKey("public-matches") }),
    queryClient.invalidateQueries({ queryKey: unscopedKey("admin-matches") }),
    queryClient.invalidateQueries({ queryKey: ["admin-matches-scoped"] }),
    queryClient.invalidateQueries({ queryKey: ["coach-matches"] }),
  ]);
}

function DeleteMatchConfirm({
  match,
  onClose,
}: {
  match: Match;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const onConfirm = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await apiFetch(`/api/matches/${encodeURIComponent(match.id)}`, {
        method: "DELETE",
        scope: false,
      });
      await invalidateAdminMatches(queryClient);
      onClose();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(
          typeof err.detail === "string"
            ? err.detail
            : "Failed to delete match.",
        );
      } else {
        setError("Failed to delete match.");
      }
      setSubmitting(false);
    }
  };

  return (
    <ConfirmDialog
      title="Delete match?"
      message={
        <>
          <p>
            <strong>
              {match.home_team || "—"} vs {match.away_team || "—"}
            </strong>{" "}
            ({match.date || "no date"}) will be permanently removed along with
            all uploaded video, HLS variants, and coaching notes referencing
            it.
          </p>
          {error ? (
            <p
              data-testid="admin-matches-delete-error"
              className="mt-2 text-destructive font-mono text-xs"
            >
              {error}
            </p>
          ) : null}
        </>
      }
      confirmLabel={submitting ? "Deleting…" : "Delete match"}
      danger
      onConfirm={onConfirm}
      onClose={onClose}
      data-testid="admin-matches-delete-confirm"
    />
  );
}

function countVodViewers(
  active: AdminStreamSession[] | undefined,
  matchId: string,
): number {
  if (!active) return 0;
  return active.filter((s) => s.match_id === matchId && s.kind !== "live")
    .length;
}

/** Aggregate a match's per-slot statuses into a single bucket the
 * filter dropdown can use. */
function deriveAggregateStatus(
  match: Match,
): "none" | "ready" | "transcoding" | "error" | "partial" {
  const statuses = Object.values(match.video_status ?? {});
  if (statuses.length === 0) return "none";
  if (statuses.every((s) => s === "ready")) return "ready";
  if (statuses.some((s) => s === "transcoding")) return "transcoding";
  if (statuses.some((s) => s === "error")) return "error";
  return "partial";
}

function deriveFormatGroup(match: Match): "full" | "two_halves" | "other" {
  if (match.format === "full") return "full";
  if (match.format === "two_halves") return "two_halves";
  return "other";
}
